/**
 * STFFLG-4163: brings a signer who opens a signing link without a live session back to that
 * document once they have signed in.
 *
 * The eSign emails carry a Review & Sign link to /signing/:requestId (mspplatform
 * ESignService.buildSigningLinkHtml). Without a session PrivateRoute sends the visitor to /signin,
 * and the sign-in pages then land them on getRouteForRole's dashboard, so the link was lost. That
 * matters most for the default EOR signer: the main agency's admin, an MSP-category role, which
 * cannot hold settings > esign_signing, so My Signatures is closed to them and the bell's eSign
 * entry is gone once it has been read. The link is their way back to the document.
 *
 * sessionStorage, not router state, because the Single Sign flow leaves the app for the identity
 * provider and comes back on a fresh /oauth/callback entry (iop-auth keeps its PKCE state the
 * same way). Only fixed, same-origin path shapes are remembered, so it is never an open redirect:
 * - a signing document: the case this exists for;
 * - STFFLG-4184, while `orderAccessInvites` is on: the two pages a job order invitation email links
 *   to (mspplatform InvitationNotifier), the invitee's Manage Orders › Pending Orders and a job
 *   order's page, so the email's button survives sign-in too. The query string is kept with the
 *   path: since Slice 5 those links carry `?agencyId=` (the branch they open in), which
 *   BranchFromLinkGate applies on arrival, only ever for a branch the user holds.
 */

import {isEnabled} from '../featureFlags'

export const POST_SIGN_IN_PATH_KEY = 'postSignInPath'

// /signing/<requestId> exactly, the route the emails and NotificationBell link to.
const SIGNING_PATH = /^\/signing\/[A-Za-z0-9_-]+\/?$/
// The invitation emails' links: View invitation / View Pending Orders, and Open order (the ROOT
// order's /manage-job-orders/summary/<jobOrderId>).
const PENDING_ORDERS_PATH = /^\/manage-orders\/pending\/?$/
const JOB_ORDER_PAGE_PATH = /^\/manage-job-orders\/summary\/[A-Za-z0-9_-]+\/?$/

function isRememberedShape(pathname: string): boolean {
  if (SIGNING_PATH.test(pathname)) return true
  // TODO(feature-flag): orderAccessInvites — remove the isEnabled check when the flag is deleted
  return (
    isEnabled('orderAccessInvites') &&
    (PENDING_ORDERS_PATH.test(pathname) || JOB_ORDER_PAGE_PATH.test(pathname))
  )
}

function toRememberedPath(pathname: string, search = ''): string | null {
  if (!isRememberedShape(pathname)) return null
  return search.startsWith('?') ? `${pathname}${search}` : pathname
}

/** Called by PrivateRoute as it sends a visitor without a session to /signin. */
export function rememberPostSignInPath(pathname: string, search = ''): void {
  const path = toRememberedPath(pathname, search)
  if (!path) return
  try {
    sessionStorage.setItem(POST_SIGN_IN_PATH_KEY, path)
  } catch {
    // Storage blocked (private mode, quota): they land on the dashboard, as before.
  }
}

/**
 * The remembered page, removed as it is read so it is used once. The sign-in pages navigate
 * here ahead of getRouteForRole. Re-validated on the way out, since anything on the page could
 * have written the key.
 */
export function consumePostSignInPath(): string | null {
  let stored: string | null = null
  try {
    stored = sessionStorage.getItem(POST_SIGN_IN_PATH_KEY)
    sessionStorage.removeItem(POST_SIGN_IN_PATH_KEY)
  } catch {
    return null
  }
  if (!stored) return null
  const queryAt = stored.indexOf('?')
  return queryAt === -1
    ? toRememberedPath(stored)
    : toRememberedPath(stored.slice(0, queryAt), stored.slice(queryAt))
}
